/**
 * ZRoles — Zod validation schemas for the Roles API (NEX-2514).
 *
 * Endpoints: /api/roles/*
 * Controller: roles/infrastructure/controllers/roles.controller.ts
 *
 * A Role is the container a company hires. It owns its resources EXCLUSIVELY —
 * a resource belongs to at most one Role — so `AssignResource` is a move, not an
 * add: pointing a resource at a new Role removes it from the old one.
 *
 * Every route sits behind the per-org `roles` feature flag. An org without the
 * flag gets a 403 with `code: "FEATURE_NOT_ENABLED"`, never an empty list.
 */

import { z } from "zod";

import { OrgRolesRollupViewSchema } from "../../../shared/domain/role-coverage/org-roles-rollup-view.schema";
import { OrgRolesRunHealthViewSchema } from "../../../shared/domain/role-coverage/org-roles-run-health-view.schema";
import {
  OrganizationAutomationSettingsInputSchema,
  OrganizationAutomationSettingsViewSchema,
  RoleSystemImpactInputSchema,
  RoleSystemImpactViewSchema,
  RoleWorkloadInputSchema,
  RoleWorkloadViewSchema
} from "../../../shared/domain/role-coverage/role-coverage-authoring-input.schema";
import { RoleCoverageViewSchema } from "../../../shared/domain/role-coverage/role-coverage-view.schema";
import { OrgRolesSystemIntegrityViewSchema } from "../../../shared/domain/role-coverage/role-system-integrity-view.schema";
import { RoleSystemMapViewSchema } from "../../../shared/domain/role-system-map/role-system-map-view.schema";
import { PermissionSetSchema } from "../../../shared/domain/roles/permission-set.types";
import { RoleCapabilitySchema } from "../../../shared/domain/roles/role-capability.types";
import {
  RoleResponsibilitiesViewSchema,
  RoleResponsibilityInputSchema,
  RoleResponsibilityPathVarsSchema,
  RoleResponsibilityViewSchema
} from "../../../shared/domain/roles/role-responsibility.schema";
import {
  RoleJobTypeInputSchema,
  RoleJobTypeLibraryViewSchema,
  RoleJobTypeViewSchema,
  RoleScopeLinesInputSchema,
  RoleScopeLinesViewSchema,
  RoleSystemPolicyInputSchema,
  RoleSystemPolicyViewSchema,
  RoleVariablesInputSchema,
  RoleVariablesViewSchema,
  RoleWorkingYearInputSchema,
  RoleWorkingYearViewSchema
} from "../../../shared/domain/roles/role-scope-authoring.schema";
import {
  RoleTasksInputSchema,
  RoleTasksViewSchema
} from "../../../shared/domain/roles/role-task.schema";
import {
  RoleTaskResponsibilitiesInputSchema,
  RoleTaskResponsibilitiesViewSchema,
  RoleTaskResponsibilityPathVarsSchema
} from "../../../shared/domain/roles/role-task-responsibility.schema";
import { ApiSuccessSchema } from "../../common/response.schema";
import {
  GrantableExternalToolsListResponseSchema,
  GrantOrgRoleCapabilityBodySchema,
  OrgRoleCallerCapabilitiesResponseSchema,
  PermissionSetCreateBodySchema,
  PermissionSetDeleteResponseSchema,
  PermissionSetMemberAddBodySchema,
  PermissionSetMemberAddResponseSchema,
  PermissionSetMemberPathVarsSchema,
  PermissionSetMemberRemoveResponseSchema,
  PermissionSetMembersListResponseSchema,
  PermissionSetPathVarsSchema,
  PermissionSetsListResponseSchema,
  PermissionSetUpdateBodySchema,
  ReviewRoleManagementRequestBodySchema,
  RevokeRoleCapabilityGrantResponseSchema,
  RoleAssignResourceBodySchema,
  RoleBackfillRbacEnforcementResponseSchema,
  RoleBoardCardMoveBodySchema,
  RoleBoardCardPathVarsSchema,
  RoleBoardCardSchema,
  RoleBoardCreateBodySchema,
  RoleBoardDeleteResponseSchema,
  RoleBoardPathVarsSchema,
  RoleBoardReorderBodySchema,
  RoleBoardSchema,
  RoleBoardsViewSchema,
  RoleBoardUpdateBodySchema,
  RoleCallerCapabilitiesResponseSchema,
  RoleCapabilityGrantPathVarsSchema,
  RoleCapabilityGrantResponseSchema,
  RoleCapabilityGrantsListResponseSchema,
  RoleCollectionGrantCreateBodySchema,
  RoleCollectionGrantDeleteResponseSchema,
  RoleCollectionGrantPathVarsSchema,
  RoleCollectionGrantSchema,
  RoleCollectionGrantsListResponseSchema,
  RoleCreateBodySchema,
  RoleCreateResultSchema,
  RoleCreationRequestSchema,
  RoleCreationRequestsListResponseSchema,
  RoleDeleteResultSchema,
  RoleDeletionInventorySchema,
  RoleDeletionPlanBodySchema,
  RoleDeletionPlanResultSchema,
  RoleDeletionRequestSchema,
  RoleDeletionRequestsListResponseSchema,
  RoleDeletionRequestsQuerySchema,
  RoleExternalToolGrantCreateBodySchema,
  RoleExternalToolGrantDeleteResponseSchema,
  RoleExternalToolGrantPathVarsSchema,
  RoleExternalToolGrantSchema,
  RoleExternalToolGrantsListResponseSchema,
  RoleJobTypePathVarsSchema,
  RoleManagementActionPathVarsSchema,
  RoleManagementActionSettingsSchema,
  RoleManagementRequestIdPathVarsSchema,
  RoleManagementRequestsQuerySchema,
  RoleManagementSettingsResponseSchema,
  RoleMemberPathVarsSchema,
  RoleMemberRemoveResponseSchema,
  RoleMemberSchema,
  RoleMembershipSchema,
  RoleMemberUpsertBodySchema,
  RolePathVarsSchema,
  RoleResourceAccessRequestCreateBodySchema,
  RoleResourceAccessRequestListQuerySchema,
  RoleResourceAccessRequestPathVarsSchema,
  RoleResourceAccessRequestReviewBodySchema,
  RoleResourceAccessRequestSchema,
  RoleResourceAccessRequestsListResponseSchema,
  RoleResourcePathVarsSchema,
  RoleResourceRetrievalConfinementResponseSchema,
  RoleRuntimeCallerResourcePathVarsSchema,
  RoleSchema,
  RolesListResponseSchema,
  RolesMineResponseSchema,
  RoleSystemImpactPathVarsSchema,
  RoleSystemLifecycleBodySchema,
  RoleSystemLifecycleResponseSchema,
  RoleTaskGraduationInputSchema,
  RoleTaskGraduationPathVarsSchema,
  RoleUnassignResourceResponseSchema,
  RoleUpdateBodySchema,
  RoleWithResourcesSchema,
  RoleWorkspaceGrantCreateBodySchema,
  RoleWorkspaceGrantDeleteResponseSchema,
  RoleWorkspaceGrantPathVarsSchema,
  RoleWorkspaceGrantSchema,
  RoleWorkspaceGrantsListResponseSchema,
  UpdateRoleManagementPolicyBodySchema
} from "./schemas";

/**
 * The capability catalogue as `GET /api/roles/capabilities` returns it.
 *
 * DECLARED HERE RATHER THAN IN `shared/domain/roles/role-capability.types.ts`,
 * and the placement is deliberate. That module is the VOCABULARY — the constant,
 * its type guard and the seeded group templates — and it is imported by the
 * backend, the SDK, the CLI and the dashboard alike. A response envelope is not
 * vocabulary, and a view schema parked in `shared/domain/**` is exactly the
 * shape that gets re-exported onto `/api/public/v1` later by a lane reading it
 * as domain rather than as transport.
 *
 * `RoleCapabilitySchema` is `z.enum(ROLE_CAPABILITIES)`, so the element type is
 * DERIVED from the catalogue rather than restated beside it: a capability added
 * to `RoleCapability` widens this response with no edit here, and one removed
 * narrows it. There is no second list to drift.
 */
const RoleCapabilityCatalogViewSchema = z.strictObject({
  /** Every capability a permission set may grant, in declaration order. */
  capabilities: z.array(RoleCapabilitySchema)
});

// ═══════════════════════════════════════════════════════════════
// ZROLES MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZRoles = {
  /**
   * GET /api/roles
   * Every Role in the organization. No membership lists — use Get for those.
   */
  List: {
    method: "GET" as const,
    path: "/api/roles",
    Response: ApiSuccessSchema(RolesListResponseSchema)
  },

  /**
   * POST /api/roles
   * Create a Role. Owner defaults to the calling user.
   */
  Create: {
    method: "POST" as const,
    path: "/api/roles",
    Body: RoleCreateBodySchema,
    Response: ApiSuccessSchema(RoleCreateResultSchema)
  },

  /**
   * DELETE /api/roles/resources/:resourceType/:resourceId
   * Detach a resource from whichever Role holds it. The resource itself
   * survives and keeps working — it simply becomes unassigned.
   *
   * Takes no roleId: exclusive ownership means there is only one Role it could
   * be leaving, so the caller does not have to know which.
   */
  UnassignResource: {
    method: "DELETE" as const,
    path: "/api/roles/resources/:resourceType/:resourceId",
    PathVars: RoleResourcePathVarsSchema,
    Response: ApiSuccessSchema(RoleUnassignResourceResponseSchema)
  },

  /**
   * GET /api/roles/resources/:resourceType/:resourceId/retrieval-confinement
   *
   * "Is this Agent or Workflow currently retrieving nothing because it holds
   * no Role?" — the two-value answer nab asked to make visible, for a resource
   * that is otherwise silently confined: no error, no log line, an empty
   * Collection or Workspace read.
   *
   * `resourceType` is narrower than every other route on this controller —
   * AGENT or WORKFLOW only, never the other three `RoleResourceType` members.
   * See `RoleRuntimeCallerResourceTypeSchema`: a Deployment, an AI task and a
   * Document template never execute, so no runtime caller identity exists for
   * one and this route has nothing to answer.
   *
   * `resourceId` is NOT checked against `RoleResource` — a resource with no
   * `RoleResource` row (an unattached resource) is exactly the interesting case, and it is
   * also not checked for existence in its own table (Agent/Workflow), because
   * this route answers a question about Role attachment, not about whether the
   * id names a real row; a nonexistent id resolves precisely as an unattached resource
   * would. The frontend already knows which ids to ask about.
   */
  GetResourceRetrievalConfinement: {
    method: "GET" as const,
    path: "/api/roles/resources/:resourceType/:resourceId/retrieval-confinement",
    PathVars: RoleRuntimeCallerResourcePathVarsSchema,
    Response: ApiSuccessSchema(RoleResourceRetrievalConfinementResponseSchema)
  },

  /**
   * GET /api/roles/:id
   * One Role WITH its resource memberships.
   */
  Get: {
    method: "GET" as const,
    path: "/api/roles/:id",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleWithResourcesSchema)
  },

  /**
   * GET /api/roles/:id/my-capabilities
   *
   * What the CALLING USER may do inside this Role. The only route in this
   * manifest whose response depends on who is asking.
   *
   * ── WHY IT EXISTS ──────────────────────────────────────────────────────────
   *
   * Every Role-scoped write is refused by `RoleCapabilityGuard` with a 403, and
   * until this route existed a client had no way to ask in advance. So a screen
   * either drew every write control and let a plain member discover the refusal
   * by clicking, or hard-coded a guess at who holds what — and a guess is
   * unmaintainable here, because a CUSTOM permission set may hold any subset of
   * the catalog. `MEMBER_CAPABILITIES` is module-private and computed
   * (`every *.view` plus `access_request.create`) precisely so that nobody
   * re-derives it; this route is how a client gets the answer instead.
   *
   * ── `my-`, NOT `capabilities` ──────────────────────────────────────────────
   *
   * The segment says whose set it is. `/api/roles/:id/capabilities` would read
   * as the Role's own catalog — a list every viewer would see identically — and
   * that is the one thing this response is not. It matches `GET /api/roles/mine`,
   * which draws the same distinction on the same surface.
   *
   * Two segments, so it cannot race `GET /api/roles/:id`; and it names `:id`, so
   * `RoleCapabilityGuard` resolves a Role from it.
   *
   * 404 for a Role that is not this organisation's, exactly as `Get` does — the
   * caller learns nothing a nonexistent Role would not also tell them.
   */
  GetMyCapabilities: {
    method: "GET" as const,
    path: "/api/roles/:id/my-capabilities",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleCallerCapabilitiesResponseSchema)
  },

  /**
   * GET /api/roles/:id/coverage
   *
   * One Role's automation coverage — `person-hours automated ÷ person-hours
   * worked` — with every figure it was derived from.
   *
   * The response is `RoleCoverageViewSchema` UNCHANGED, imported rather than
   * restated. That module is the only thing that produces these numbers and it
   * produces them identically in the browser and on the server; a second
   * spelling of the shape here would be a second source of truth for a
   * customer-facing ROI figure, and the two would drift on the first field
   * either side gained.
   *
   * READ THE `coverage` DISCRIMINANT, NEVER A NULLABLE NUMBER. A Role with no
   * workload model has NO percentage — not 0%, not 100% — and the union is what
   * stops a consumer rendering "not modelled" as a confident zero. The
   * assumptions the figure rests on (`workingTime`) and the authored
   * denominator (`workload`) ship in the same response on purpose: a reader
   * shown "18.29%" needs "12 people × 35 h/week × 46 weeks" under it, or the
   * number is an assertion.
   *
   * 404 for a Role that is not this organisation's, exactly as `Get` does.
   */
  GetCoverage: {
    method: "GET" as const,
    path: "/api/roles/:id/coverage",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleCoverageViewSchema)
  },

  /**
   * PATCH /api/roles/:id
   * Rename a Role, rewrite its job description, or hand it to a new owner.
   */
  Update: {
    method: "PATCH" as const,
    path: "/api/roles/:id",
    PathVars: RolePathVarsSchema,
    Body: RoleUpdateBodySchema,
    Response: ApiSuccessSchema(RoleSchema)
  },

  /**
   * DELETE /api/roles/:id
   * Delete the container. Its resources are unassigned, never deleted.
   */
  Delete: {
    method: "DELETE" as const,
    path: "/api/roles/:id",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleDeleteResultSchema)
  },

  /**
   * GET /api/roles/:id/deletion-inventory
   * Every re-homable element this Role holds, across all FOUR placement tables,
   * each carrying what RELEASING it would actually cost — plus the other Roles
   * an element can be shipped to.
   *
   * ── WHY A SEPARATE READ RATHER THAN A FIELD ON `Get` ────────────────────────
   *
   * `Get` is the Role detail pane, served on every Role screen, and it is also
   * `public/v1`'s authorize step. This read names every element, resolves every
   * name and asks the organization's resource-visibility policy per resource
   * type — several queries that no other screen needs. Folding it into `Get`
   * would make every Role page and every v1 Roles request pay for a wizard
   * nobody is running.
   *
   * AN EMPTY `items` ARRAY IS THE SKIP SIGNAL — the existing type-to-confirm
   * delete panel is already correct for a Role holding nothing re-homable, and
   * there is deliberately no second deletion path.
   */
  GetDeletionInventory: {
    method: "GET" as const,
    path: "/api/roles/:id/deletion-inventory",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleDeletionInventorySchema)
  },

  /**
   * POST /api/roles/:id/deletion-plan
   * Apply every decision, then delete the Role — one transaction, or none of it.
   *
   * The body must name EVERY element the inventory returned; an incomplete plan
   * is refused rather than defaulted, because an omitted element and an element
   * the caller never saw are the same request.
   *
   * ⚠️ THIS SHARES `DELETE /api/roles/:id`'s GOVERNANCE BRANCH AND MUST. Deleting
   * a Role can require approval, and on that branch the Role is still standing —
   * so NOTHING is applied and the result carries the request id instead. Moving
   * a customer's resources between Roles on the strength of a deletion that may
   * never be approved is the failure this shape makes unrepresentable.
   */
  ExecuteDeletionPlan: {
    method: "POST" as const,
    path: "/api/roles/:id/deletion-plan",
    PathVars: RolePathVarsSchema,
    Body: RoleDeletionPlanBodySchema,
    Response: ApiSuccessSchema(RoleDeletionPlanResultSchema)
  },

  /**
   * POST /api/roles/:id/pause
   * Stop this Role's work. Its agents and workflows refuse to execute until it
   * is resumed; nothing it grants is touched.
   *
   * ── WHY POST AND NOT A `PATCH` FIELD ────────────────────────────────────
   *
   * A named action rather than a partial write of the Role's representation,
   * for the same reason `AssignResource` is one: the caller is not editing a
   * value, it is asking for a state change with a side effect on execution, and
   * the two carry DIFFERENT capabilities (`role.pause`, not `role.update`).
   * Folding it into `Update`'s body would put one route under two capabilities
   * and force the guard to read the body to know which — the exact shape
   * `RoleCapability`'s header refuses.
   *
   * ── NO BODY, DELIBERATELY ───────────────────────────────────────────────
   *
   * There is nothing for a caller to say. The actor is the JWT's subject and
   * the instant is the server's clock; accepting either from the body would be
   * a caller-supplied audit trail, which is no audit trail.
   *
   * ── IDEMPOTENT, AND THE RESPONSE IS THE STORED STATE ────────────────────
   *
   * 🚨 PAUSING AN ALREADY-PAUSED ROLE MUST NOT RE-STAMP IT. `pausedAt` answers
   * "since when", so a second call — a double-click, a retried request, two
   * operators reacting to the same incident — that moved the timestamp would
   * destroy the only fact the column exists to carry. The write is conditional
   * on `pausedAt IS NULL` and the response is read back off the row, so both
   * callers are answered with the ORIGINAL stop and neither is told it failed.
   * That is the server-side latch; the client's own is a second belt.
   */
  Pause: {
    method: "POST" as const,
    path: "/api/roles/:id/pause",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleSchema)
  },

  /**
   * POST /api/roles/:id/resume
   * Start this Role's work again.
   *
   * Idempotent in the same way and for a weaker reason — resuming a running
   * Role clears a column that is already `null`, so there is no fact to
   * destroy. It is conditional anyway, so that the two routes are one shape and
   * a reader does not have to work out which of the pair is the careful one.
   */
  Resume: {
    method: "POST" as const,
    path: "/api/roles/:id/resume",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleSchema)
  },

  /**
   * POST /api/roles/:id/resources
   * Put a resource in this Role. Exclusive ownership makes this a MOVE: if the
   * resource already belongs to another Role, it leaves that one.
   */
  AssignResource: {
    method: "POST" as const,
    path: "/api/roles/:id/resources",
    PathVars: RolePathVarsSchema,
    Body: RoleAssignResourceBodySchema,
    Response: ApiSuccessSchema(z.object({ success: z.literal(true) }))
  },

  // ─────────────────────────────────────────────────────────────
  // Membership tiers (NEX-2514 member-view slice)
  // ─────────────────────────────────────────────────────────────

  /**
   * GET /api/roles/mine
   * Every Role the caller owns, admins, or is a plain member of. Lives on
   * RolesController (not the membership controller) and is registered ahead
   * of `Get` there — "mine" is a static segment at the same position as `:id`,
   * so registration order is what makes it win the match.
   */
  ListMine: {
    method: "GET" as const,
    path: "/api/roles/mine",
    Response: ApiSuccessSchema(RolesMineResponseSchema)
  },

  /**
   * GET /api/roles/system-map
   *
   * Every resource this organization's workflows reach, and every arrow between
   * them, derived on read from `Workflow.nodes`. No table backs this.
   *
   * ORG-SCOPED, NOT ROLE-SCOPED, and that is the design rather than a
   * convenience. A Role owns its resources exclusively, so a Role-scoped map
   * could only show arrows that stay inside one Role — precisely the arrows
   * nobody needs a map to understand. The value is the CROSSING: a Support
   * workflow invoking a Billing AI task appears in neither Role's own view.
   * Each node carries `roleId` so the crossing is visible.
   *
   * Takes no parameters: the organization comes from the JWT via
   * `@CurrentOrganizationId()`, never from the caller.
   *
   * ⚠️ Static segment at the same position as `:id`, exactly like `mine` and
   * `resources`, so its handler MUST be declared before `@Get(":id")` on
   * `RolesController` or `system-map` is swallowed as a Role id.
   */
  SystemMap: {
    method: "GET" as const,
    path: "/api/roles/system-map",
    Response: ApiSuccessSchema(RoleSystemMapViewSchema)
  },

  /**
   * GET /api/roles/capabilities
   *
   * The capability CATALOGUE — every `<area>.<verb>` a permission set may
   * grant, in declaration order.
   *
   * ── IT PUBLISHES A VOCABULARY THAT ALREADY EXISTS, AND INVENTS NOTHING ────
   *
   * `ROLE_CAPABILITIES` (`shared/domain/roles/role-capability.types.ts`) is
   * `Object.values(RoleCapability)` and already ships to callers through
   * `@nexus/types`, the SDK and the dashboard. What it had no route for was an
   * API-key caller, who cannot import a TypeScript constant — so
   * `POST /api/roles/:id/groups` asked for a `capabilities` array whose legal
   * values were readable nowhere over HTTP. This route is the missing half of
   * that write, and nothing else.
   *
   * 🚨 THE STATED BLOCKER FOR THIS ROUTE — "the router reads `capabilities` as
   * a Role id" — IS FALSE, AND DECLARATION ORDER IS THE WHOLE FIX. `mine`,
   * `system-map` and `management` are three static single segments already
   * beating `@Get(":id")` in `RolesController`, and the ONLY mechanism that
   * decides that race is the order handlers are declared inside that one class.
   * `roles-static-routes-win-over-the-id-catch-all.spec.ts` asks the real
   * Express router which layer wins, and this route is pinned there beside its
   * three siblings. Module ordering in `app.module.ts` is NOT the mechanism and
   * reads as though it is.
   *
   * ── ORG NAMESPACE, AND THERE IS DELIBERATELY NOTHING TO SCOPE ─────────────
   *
   * The catalogue is a property of the BUILD, not of a tenant: it takes no path
   * var, no query, no body, reads no table, and returns the same array for
   * every caller. So no `organizationId` filter is missing — there is no row to
   * filter — and no Role-scoped capability can gate it, because it names no
   * Role. It is also not a disclosure: a member already sees these strings on
   * the Role's own group-settings screen.
   *
   * ⚠️ NO `area` FIELD, ON PURPOSE. The area is the half before the dot and the
   * column's own CHECK guarantees that shape, so a server-side `area` would be
   * a second source of truth for a `split(".")`. The dashboard already derives
   * it (`capabilityAreas.ts`, `RoleCapabilityArea`) and any caller can.
   */
  ListRoleCapabilities: {
    method: "GET" as const,
    path: "/api/roles/capabilities",
    Response: ApiSuccessSchema(RoleCapabilityCatalogViewSchema)
  },

  /**
   * GET /api/roles/capabilities/mine
   *
   * Which of that catalogue the CALLING USER holds across the organisation,
   * naming no Role.
   *
   * ── WHY IT EXISTS ──────────────────────────────────────────────────────────
   *
   * `GetMyCapabilities` answers the same question inside ONE Role and needs a
   * `:id` to do it. A capability that names no Role therefore has no client-side
   * answer at all — and `role.create` is exactly that: a Role being created has
   * no id, so `RoleCapabilityGuard` evaluates `POST /api/roles` with
   * `roleId: null` against the org scope alone. The consequence shipped: the New
   * Role control was drawn for every reader and the 403 arrived after the click,
   * which is the defect `GetMyCapabilities` was built to end, surviving one scope
   * over because the read that would answer it could not be asked.
   *
   * ── THE PATH, AND WHY IT IS NOT `/api/roles/my-capabilities` ───────────────
   *
   * 🚨 THAT SPELLING IS A SINGLE STATIC SEGMENT UNDER `/roles`, SO IT RACES
   * `GET /roles/:id` — AND THIS ROUTE CANNOT WIN THAT RACE THE WAY ITS FOUR
   * SIBLINGS DO. `mine`, `system-map`, `management` and `capabilities` all beat
   * the catch-all because they are declared ABOVE it inside `RolesController`,
   * and declaration order within one class is the only mechanism that decides it.
   * This route belongs on `RoleCapabilitiesController` — the controller for
   * per-viewer answers — so the two handlers sit in DIFFERENT classes and the
   * order becomes Nest's controller-registration order, which
   * `roles-static-routes-win-over-the-id-catch-all.spec.ts` states outright is
   * not a mechanism to rely on. A shadowed route is also invisible over the wire:
   * the guard answers before either handler runs, so a dead endpoint and a live
   * one are byte-identical.
   *
   * TWO SEGMENTS REMOVES THE RACE RATHER THAN PINNING IT. Nothing matches
   * `/roles/capabilities/mine` but this route: `/roles/:id` is one segment, and
   * every two-segment sibling (`:id/my-capabilities`, `:id/coverage`,
   * `:id/members`, …) fixes its SECOND segment to a literal that is not `mine`.
   * The nesting also reads correctly — it is a projection of the catalogue
   * directly above, and `mine` already means "scoped to the caller" on this
   * surface (`GET /api/roles/mine`).
   *
   * ── NO 404, AND NOTHING TO SCOPE BEYOND THE ORGANISATION ───────────────────
   *
   * The response is about the caller and the organisation in their JWT. There is
   * no id in the path, so there is no resource whose existence a status could
   * disclose and no tenancy question beyond the anchor: a caller holding nothing
   * gets an empty array, which is the same "no" every client already renders.
   * It discloses nothing the caller does not already hold.
   */
  GetMyOrgCapabilities: {
    method: "GET" as const,
    path: "/api/roles/capabilities/mine",
    Response: ApiSuccessSchema(OrgRoleCallerCapabilitiesResponseSchema)
  },

  /**
   * GET /api/roles/:id/members
   * Who's in this Role: owner (from Role.ownerUserId) plus admins/members.
   */
  ListMembers: {
    method: "GET" as const,
    path: "/api/roles/:id/members",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleMembershipSchema)
  },

  /**
   * POST /api/roles/:id/members
   * Add a user as ADMIN or MEMBER, or change their tier — upsert, matching
   * AssignResource's upsert-as-move pattern. Refused (409) if the user is
   * already this Role's owner.
   */
  UpsertMember: {
    method: "POST" as const,
    path: "/api/roles/:id/members",
    PathVars: RolePathVarsSchema,
    Body: RoleMemberUpsertBodySchema,
    Response: ApiSuccessSchema(RoleMemberSchema)
  },

  /**
   * DELETE /api/roles/:id/members/:userId
   * Remove a user's ADMIN/MEMBER standing. Does not touch ownership.
   */
  RemoveMember: {
    method: "DELETE" as const,
    path: "/api/roles/:id/members/:userId",
    PathVars: RoleMemberPathVarsSchema,
    Response: ApiSuccessSchema(RoleMemberRemoveResponseSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // Permission sets (RoleGroup) — see the block comment above
  // `PermissionSetsListResponseSchema`. `/api/roles/:id/group-grants` used to
  // live here (an org UserGroup reaching one surface of this Role) and does
  // not any more — `RoleGroupGrant` folded into this model, a custom
  // permission set with one `group` member.
  // ─────────────────────────────────────────────────────────────

  /**
   * GET /api/roles/:id/groups
   * Every permission set on this Role — the two seeded templates plus every
   * custom group — ordered by `position`, each with its capabilities, its
   * surfaces and how many subjects sit in it.
   *
   * ONE CALL RENDERS THE WHOLE PANEL. The capability catalog it is displayed
   * against is a compile-time constant in `@nexus/types` (`ROLE_CAPABILITIES`),
   * so there is no second request and no server round trip for a closed set.
   */
  ListGroups: {
    method: "GET" as const,
    path: "/api/roles/:id/groups",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(PermissionSetsListResponseSchema)
  },

  /**
   * POST /api/roles/:id/groups
   * Create a custom permission set on this Role. Always `isSystem = false`.
   */
  CreateGroup: {
    method: "POST" as const,
    path: "/api/roles/:id/groups",
    PathVars: RolePathVarsSchema,
    Body: PermissionSetCreateBodySchema,
    Response: ApiSuccessSchema(PermissionSetSchema)
  },

  /**
   * PATCH /api/roles/:id/groups/:groupId
   * Edit a custom permission set. Arrays are full replacements; a system group
   * is refused (409).
   */
  UpdateGroup: {
    method: "PATCH" as const,
    path: "/api/roles/:id/groups/:roleGroupId",
    PathVars: PermissionSetPathVarsSchema,
    Body: PermissionSetUpdateBodySchema,
    Response: ApiSuccessSchema(PermissionSetSchema)
  },

  /**
   * DELETE /api/roles/:id/groups/:groupId
   * Destroy a custom permission set and its capability, surface and member rows
   * (Postgres cascades all three). A system group is refused (409).
   */
  DeleteGroup: {
    method: "DELETE" as const,
    path: "/api/roles/:id/groups/:roleGroupId",
    PathVars: PermissionSetPathVarsSchema,
    Response: ApiSuccessSchema(PermissionSetDeleteResponseSchema)
  },

  /**
   * GET /api/roles/:id/groups/:roleGroupId/members
   * One permission set's roster.
   *
   * A 404 ON AN UNKNOWN SET, never `[]` — an empty set is a legitimate state, so
   * the empty list would otherwise mean two different things. Unlike its three
   * siblings above this is a member-facing READ, so it carries `group.view` and
   * no management action.
   */
  ListGroupMembers: {
    method: "GET" as const,
    path: "/api/roles/:id/groups/:roleGroupId/members",
    PathVars: PermissionSetPathVarsSchema,
    Response: ApiSuccessSchema(PermissionSetMembersListResponseSchema)
  },

  /**
   * POST /api/roles/:id/groups/:roleGroupId/members
   * Put one user into this permission set.
   *
   * 🚨 GATED `TEAM_MANAGE`, NOT `GROUP_MANAGE` — deciding who holds a bundle is a
   * membership act, and the four routes above that define what the bundle grants
   * are a different authority. Its public-v1 twin carries the same capability.
   *
   * IDEMPOTENT — `added: false` for somebody already in the set, and still a 201.
   * The user must be a member of the organization or own the Role; anybody else
   * answers 404 with the same body an unknown set gets.
   *
   * ON A SET THAT SHIPS WITH NEXUS IT SETS THE USER'S TIER — `maintainer` makes
   * them ADMIN, `member` makes them MEMBER, the other shipped seat goes, and they
   * join the Role if they were not in it. The Role's owner is a no-op.
   */
  AddGroupMember: {
    method: "POST" as const,
    path: "/api/roles/:id/groups/:roleGroupId/members",
    PathVars: PermissionSetPathVarsSchema,
    Body: PermissionSetMemberAddBodySchema,
    Response: ApiSuccessSchema(PermissionSetMemberAddResponseSchema)
  },

  /**
   * DELETE /api/roles/:id/groups/:roleGroupId/members/:userId
   * Take one user out of this permission set.
   *
   * THE NARROW REVOCATION, and `DeleteGroup` is not its substitute — destroying
   * the set cascades every member's row. On a CUSTOM set it does not touch the
   * user's standing in the Role; `RemoveMember` is that route.
   *
   * ON A SET THAT SHIPS WITH NEXUS IT IS A MEMBERSHIP CHANGE, because that seat is
   * the user's tier: their only such seat → they leave the Role exactly as
   * `RemoveMember` would, every permission-set seat included; both shipped seats →
   * only this one goes and their tier becomes the other set's. The Role's owner is
   * a no-op.
   */
  RemoveGroupMember: {
    method: "DELETE" as const,
    path: "/api/roles/:id/groups/:roleGroupId/members/:userId",
    PathVars: PermissionSetMemberPathVarsSchema,
    Response: ApiSuccessSchema(PermissionSetMemberRemoveResponseSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // Collection grants (NEX-2514 L5 grants slice)
  // ─────────────────────────────────────────────────────────────

  /**
   * GET /api/roles/:id/collection-grants
   * Every knowledge Collection granted onto this Role.
   */
  ListCollectionGrants: {
    method: "GET" as const,
    path: "/api/roles/:id/collection-grants",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleCollectionGrantsListResponseSchema)
  },

  /**
   * POST /api/roles/:id/collection-grants
   * Grant a Collection to this Role. Idempotent — regranting an already-granted
   * Collection returns the existing row.
   */
  CreateCollectionGrant: {
    method: "POST" as const,
    path: "/api/roles/:id/collection-grants",
    PathVars: RolePathVarsSchema,
    Body: RoleCollectionGrantCreateBodySchema,
    Response: ApiSuccessSchema(RoleCollectionGrantSchema)
  },

  /**
   * DELETE /api/roles/:id/collection-grants/:grantId
   * Revoke one Collection's grant on this Role.
   */
  DeleteCollectionGrant: {
    method: "DELETE" as const,
    path: "/api/roles/:id/collection-grants/:grantId",
    PathVars: RoleCollectionGrantPathVarsSchema,
    Response: ApiSuccessSchema(RoleCollectionGrantDeleteResponseSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // External tool grants — the third M:N grant surface
  //
  // `external_tool` used to be a `RoleResourceType` and was unwritable: that
  // table's `@@unique([organizationId, resourceType, resourceId])` is exclusive
  // attachment, and the requirement is that several Roles hold one tool. The
  // grant tables are where a shared system lives; see
  // `schemas/external-tool-grants.ts` for why the grant names the CATALOGUE row
  // rather than the org's connection.
  // ─────────────────────────────────────────────────────────────

  /**
   * GET /api/roles/:id/grantable-external-tools
   * The tools this organisation could put into a Role — the ones it OWNS plus
   * the ones it has CONNECTED — each flagged with whether THIS Role holds it.
   *
   * 🔴 DELIBERATELY NARROWER THAN `CreateExternalToolGrant` ADMITS. That write
   * accepts anything the three-class rule reaches, which is the whole 2,747-row
   * shared catalogue; a picker over that is not a picker. The two questions are
   * allowed to have different answers — see `schemas/grantable-external-tools.ts`.
   */
  ListGrantableExternalTools: {
    method: "GET" as const,
    path: "/api/roles/:id/grantable-external-tools",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(GrantableExternalToolsListResponseSchema)
  },

  /**
   * GET /api/roles/:id/external-tool-grants
   * Every external tool granted onto this Role.
   */
  ListExternalToolGrants: {
    method: "GET" as const,
    path: "/api/roles/:id/external-tool-grants",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleExternalToolGrantsListResponseSchema)
  },

  /**
   * POST /api/roles/:id/external-tool-grants
   * Grant an external tool to this Role. Idempotent — regranting an
   * already-granted tool returns the existing row.
   */
  CreateExternalToolGrant: {
    method: "POST" as const,
    path: "/api/roles/:id/external-tool-grants",
    PathVars: RolePathVarsSchema,
    Body: RoleExternalToolGrantCreateBodySchema,
    Response: ApiSuccessSchema(RoleExternalToolGrantSchema)
  },

  /**
   * DELETE /api/roles/:id/external-tool-grants/:grantId
   * Revoke one external tool's grant on this Role.
   */
  DeleteExternalToolGrant: {
    method: "DELETE" as const,
    path: "/api/roles/:id/external-tool-grants/:grantId",
    PathVars: RoleExternalToolGrantPathVarsSchema,
    Response: ApiSuccessSchema(RoleExternalToolGrantDeleteResponseSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // Workspace grants (NEX-2514 L5 grants slice)
  // ─────────────────────────────────────────────────────────────

  /**
   * GET /api/roles/:id/workspace-grants
   * Every drive/file Workspace granted onto this Role.
   */
  ListWorkspaceGrants: {
    method: "GET" as const,
    path: "/api/roles/:id/workspace-grants",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleWorkspaceGrantsListResponseSchema)
  },

  /**
   * POST /api/roles/:id/workspace-grants
   * Grant a Workspace to this Role. Idempotent — regranting an already-granted
   * Workspace returns the existing row.
   */
  CreateWorkspaceGrant: {
    method: "POST" as const,
    path: "/api/roles/:id/workspace-grants",
    PathVars: RolePathVarsSchema,
    Body: RoleWorkspaceGrantCreateBodySchema,
    Response: ApiSuccessSchema(RoleWorkspaceGrantSchema)
  },

  /**
   * DELETE /api/roles/:id/workspace-grants/:grantId
   * Revoke one Workspace's grant on this Role.
   */
  DeleteWorkspaceGrant: {
    method: "DELETE" as const,
    path: "/api/roles/:id/workspace-grants/:grantId",
    PathVars: RoleWorkspaceGrantPathVarsSchema,
    Response: ApiSuccessSchema(RoleWorkspaceGrantDeleteResponseSchema)
  },

  /**
   * GET /api/roles/:id/access-requests
   * A member's requests for access to this Role's resources.
   */
  ListAccessRequests: {
    method: "GET" as const,
    path: "/api/roles/:id/access-requests",
    PathVars: RolePathVarsSchema,
    Query: RoleResourceAccessRequestListQuerySchema,
    Response: ApiSuccessSchema(RoleResourceAccessRequestsListResponseSchema)
  },

  /**
   * POST /api/roles/:id/access-requests
   * A member asks for access to one of the Role's resources. Sits PENDING
   * until an admin reviews it. Refused (409) if an identical request from the
   * same caller is already PENDING.
   */
  CreateAccessRequest: {
    method: "POST" as const,
    path: "/api/roles/:id/access-requests",
    PathVars: RolePathVarsSchema,
    Body: RoleResourceAccessRequestCreateBodySchema,
    Response: ApiSuccessSchema(RoleResourceAccessRequestSchema)
  },

  /**
   * PATCH /api/roles/:id/access-requests/:requestId
   * Approve or reject a PENDING request. A request is reviewed exactly once —
   * reviewing an already-reviewed request is refused (409).
   */
  ReviewAccessRequest: {
    method: "PATCH" as const,
    path: "/api/roles/:id/access-requests/:requestId",
    PathVars: RoleResourceAccessRequestPathVarsSchema,
    Body: RoleResourceAccessRequestReviewBodySchema,
    Response: ApiSuccessSchema(RoleResourceAccessRequestSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // RBAC enforcement (NEX-2514 L4)
  // ─────────────────────────────────────────────────────────────

  /**
   * POST /api/roles/backfill-rbac-enforcement
   * Syncs every RoleResource already in the org as if freshly attached.
   * `roles` only gates NEW attach/detach/approve events —
   * flipping it on does not retroactively touch resources a Role already
   * held. Run this once per org right after enabling the flag, or any time
   * a resync is needed. Idempotent — safe to re-run.
   */
  BackfillRbacEnforcement: {
    method: "POST" as const,
    path: "/api/roles/backfill-rbac-enforcement",
    Response: ApiSuccessSchema(RoleBackfillRbacEnforcementResponseSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // Management governance (NEX-3199) — org:admin only, always
  // ─────────────────────────────────────────────────────────────

  /**
   * GET /api/roles/management
   * The approval setting for each action that has an approval queue —
   * CREATE_ROLE and DELETE_ROLE — in one read, which is what the settings panel
   * renders. See `APPROVAL_QUEUED_ROLE_MANAGEMENT_ACTIONS` for why the other
   * three RoleManagementAction values are absent rather than reported empty.
   */
  GetManagementSettings: {
    method: "GET" as const,
    path: "/api/roles/management",
    Response: ApiSuccessSchema(RoleManagementSettingsResponseSchema)
  },

  /**
   * PATCH /api/roles/management/:action/policy
   * CREATE_ROLE and DELETE_ROLE only. `:action` still validates against the FULL
   * five-value enum on purpose: a retired value has to reach the handler to be
   * REFUSED there, rather than 404ing as a malformed path and reading like a
   * routing bug.
   */
  UpdateManagementPolicy: {
    method: "PATCH" as const,
    path: "/api/roles/management/:action/policy",
    PathVars: RoleManagementActionPathVarsSchema,
    Body: UpdateRoleManagementPolicyBodySchema,
    Response: ApiSuccessSchema(RoleManagementActionSettingsSchema)
  },

  /** GET /api/roles/management/creation-requests — omit `status` for every request, not just PENDING. */
  ListCreationRequests: {
    method: "GET" as const,
    path: "/api/roles/management/creation-requests",
    Query: RoleManagementRequestsQuerySchema,
    Response: ApiSuccessSchema(RoleCreationRequestsListResponseSchema)
  },

  /**
   * PATCH /api/roles/management/creation-requests/:id
   * Approve creates the Role now (it does not exist until this happens) and
   * backfills `createdRoleId`; reject just marks REJECTED.
   */
  ReviewCreationRequest: {
    method: "PATCH" as const,
    path: "/api/roles/management/creation-requests/:id",
    PathVars: RoleManagementRequestIdPathVarsSchema,
    Body: ReviewRoleManagementRequestBodySchema,
    Response: ApiSuccessSchema(RoleCreationRequestSchema)
  },

  /** GET /api/roles/management/deletion-requests — omit `status` for every request, not just PENDING. */
  ListDeletionRequests: {
    method: "GET" as const,
    path: "/api/roles/management/deletion-requests",
    Query: RoleDeletionRequestsQuerySchema,
    Response: ApiSuccessSchema(RoleDeletionRequestsListResponseSchema)
  },

  /**
   * PATCH /api/roles/management/deletion-requests/:id
   * Approve deletes the Role now; reject just marks REJECTED and the Role
   * survives untouched.
   */
  ReviewDeletionRequest: {
    method: "PATCH" as const,
    path: "/api/roles/management/deletion-requests/:id",
    PathVars: RoleManagementRequestIdPathVarsSchema,
    Body: ReviewRoleManagementRequestBodySchema,
    Response: ApiSuccessSchema(RoleDeletionRequestSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // Org-wide capability grants — `RoleCapabilityGrant`, org:admin only
  // ─────────────────────────────────────────────────────────────
  //
  // 🔴 THE GATE IS `OrgAdminGuard` AND IT MUST NEVER BECOME A CAPABILITY.
  // These three routes write the table that decides who holds what across
  // EVERY Role in the organisation, so a `@RequireRoleCapability(X)` gate here
  // would let any holder of X grant themselves — and anyone else — the rest of
  // the catalog, permanently. `role-capability-grants.controller.ts` carries
  // the full argument and the self-escalation analysis.
  //
  // NESTED UNDER `/management` and not at `/api/roles/capability-grants`. A
  // single static segment under `roles/` races `RolesController`'s
  // `@Get(":id")`, and that race is decided by module-CONTAINER insertion order
  // across controllers — which `roles-static-routes-win-over-the-id-catch-all.spec.ts`
  // cannot see, because it loads one controller. `/api/roles/capabilities/mine`
  // pays two segments for the same reason. Three segments here, so there is no
  // race to lose.

  /**
   * GET /api/roles/management/capability-grants
   * Every org-wide capability grant in the organisation, with its subject —
   * what the grants editor renders.
   *
   * 🚨 `grants` IS NULLABLE, AND THE NULL IS THE TABLE BEING UNREADABLE.
   * `RoleCapabilityGrant`'s migration ships alongside its readers, so a pod can
   * serve requests against a database with no such relation. `{ grants: [] }`
   * there would tell an administrator that this organisation has granted
   * nothing over a table nobody asked, and invite them to re-grant rows that
   * already exist. `[]` means read-and-empty, always.
   */
  ListOrgCapabilityGrants: {
    method: "GET" as const,
    path: "/api/roles/management/capability-grants",
    Response: ApiSuccessSchema(RoleCapabilityGrantsListResponseSchema)
  },

  /**
   * POST /api/roles/management/capability-grants
   * Grant one capability to one subject, org-wide. Idempotent: an upsert on
   * `(organizationId, capability, subjectType, subjectId)`, so re-granting
   * returns the existing row rather than a duplicate or a 409.
   *
   * ⚠️ THAT IDEMPOTENCE DEPENDS ON `subjectId` BEING NOT NULL, which is why
   * this contract's org-wide arm carries no `subjectId` at all — see
   * `GrantOrgRoleCapabilityBodySchema`. A Postgres unique index is NULLS
   * DISTINCT, so a null-spelled org row is unconstrained by that key and every
   * upsert would insert a fresh duplicate.
   *
   * A `subjectId` naming a user or group outside the caller's organisation is a
   * 404, identical to one naming nothing at all — the caller learns nothing
   * about another tenant's people.
   */
  GrantOrgCapability: {
    method: "POST" as const,
    path: "/api/roles/management/capability-grants",
    Body: GrantOrgRoleCapabilityBodySchema,
    Response: ApiSuccessSchema(RoleCapabilityGrantResponseSchema)
  },

  /**
   * DELETE /api/roles/management/capability-grants/:grantId
   * Revoke one grant. Idempotent — `{ revoked: false }` for a grant that was
   * already gone, and for one belonging to another organisation, which are
   * deliberately the same answer.
   */
  RevokeOrgCapabilityGrant: {
    method: "DELETE" as const,
    path: "/api/roles/management/capability-grants/:grantId",
    PathVars: RoleCapabilityGrantPathVarsSchema,
    Response: ApiSuccessSchema(RevokeRoleCapabilityGrantResponseSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // Boards — the Overview screen's lanes (NEX-2514 layout slice)
  // ─────────────────────────────────────────────────────────────
  //
  // EVERY BOARD ROUTE IS NESTED UNDER `:id`, AND THAT IS A ROUTING DECISION
  // RATHER THAN A STYLE ONE. `RolesController`'s `@Get(":id")` swallows any
  // single-segment literal declared under `roles/` in a DIFFERENT module —
  // `/api/roles/system-map` and `/api/roles/management` both died that way,
  // because Nest registers routes in container INSERTION order and a module
  // reached transitively from an earlier import is inserted at that earlier
  // point, so `imports`-array order does not fix it. `:id` matches exactly one
  // segment, so `roles/:id/boards` cannot collide with it and this controller
  // needs no declaration-order rescue in a class it does not own.
  //
  // Reorder is `PUT` ON THE COLLECTION for the same reason: `boards/order` would
  // put a literal back beside `:boardId` and buy a race for nothing. `PUT` says
  // "this is the order", which is exactly what the body asserts.

  /**
   * GET /api/roles/:id/boards
   * The Role's lanes and every card's placement, in one answer. See
   * `RoleBoardsViewSchema` for why the two travel together and why this carries
   * no resource metadata.
   *
   * 404 for a Role that is not this organization's, exactly as `Get` does.
   */
  ListBoards: {
    method: "GET" as const,
    path: "/api/roles/:id/boards",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleBoardsViewSchema)
  },

  /**
   * POST /api/roles/:id/boards
   * Append a lane. Position is assigned server-side; ordering is `ReorderBoards`'
   * job.
   */
  CreateBoard: {
    method: "POST" as const,
    path: "/api/roles/:id/boards",
    PathVars: RolePathVarsSchema,
    Body: RoleBoardCreateBodySchema,
    Response: ApiSuccessSchema(RoleBoardSchema)
  },

  /**
   * PUT /api/roles/:id/boards
   * Renumber the whole list from 0 in the order given, in one statement.
   * `409 ROLE_BOARD_ORDER_STALE` when the submitted set is not the Role's
   * current board set.
   */
  ReorderBoards: {
    method: "PUT" as const,
    path: "/api/roles/:id/boards",
    PathVars: RolePathVarsSchema,
    Body: RoleBoardReorderBodySchema,
    Response: ApiSuccessSchema(RoleBoardsViewSchema)
  },

  /**
   * PATCH /api/roles/:id/boards/:boardId
   * Rename and/or recolour one lane.
   */
  UpdateBoard: {
    method: "PATCH" as const,
    path: "/api/roles/:id/boards/:boardId",
    PathVars: RoleBoardPathVarsSchema,
    Body: RoleBoardUpdateBodySchema,
    Response: ApiSuccessSchema(RoleBoardSchema)
  },

  /**
   * DELETE /api/roles/:id/boards/:boardId
   *
   * Deleting a lane returns its cards to Ungrouped; it never deletes a card.
   * The card→board foreign keys are `NO ACTION`, so the nulling and the delete
   * are ONE transaction and forgetting the first half raises `23503` rather than
   * unattacheding a card. `cardsUnplaced` is how many fell to Ungrouped.
   */
  DeleteBoard: {
    method: "DELETE" as const,
    path: "/api/roles/:id/boards/:boardId",
    PathVars: RoleBoardPathVarsSchema,
    Response: ApiSuccessSchema(RoleBoardDeleteResponseSchema)
  },

  /**
   * PATCH /api/roles/:id/cards/:cardType/:cardId
   *
   * Drag one card into a lane, or out of every lane with `boardId: null`. The
   * `(cardType, cardId)` pair is the one the Overview screen already keys its
   * nodes on, so a caller never has to learn a placement row id and never has to
   * know which of the three tables holds it.
   *
   * A `cardType` outside the eight placeable kinds is a 400, not a silent no-op —
   * see `RoleBoardCardTypeSchema`.
   *
   * LAST WRITE WINS, PER CARD, AND THAT IS THE WHOLE SEMANTIC. A card belongs to
   * exactly one board, so two people dragging the SAME card have no merge
   * between them — one of the two arrangements is the answer and it is the later
   * one. Two people dragging DIFFERENT cards never interact at all. There is no
   * per-card ordinal to conflict over: cards are sorted client-side and no
   * position is stored, deliberately.
   */
  MoveBoardCard: {
    method: "PATCH" as const,
    path: "/api/roles/:id/cards/:cardType/:cardId",
    PathVars: RoleBoardCardPathVarsSchema,
    Body: RoleBoardCardMoveBodySchema,
    Response: ApiSuccessSchema(RoleBoardCardSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // Coverage authoring — the WRITE side of the ROI model
  //
  // The three tables behind every coverage figure shipped with a migration, a
  // read path and no way to put a number in any of them. These four routes are
  // that way in. Each body is validated against `CoverageFormulaSchema` before
  // it reaches a column — see `role-coverage-authoring-input.schema.ts` for why
  // a write boundary refuses what the read boundary degrades around.
  // ─────────────────────────────────────────────────────────────

  /**
   * GET /api/organization-automation-settings
   *
   * The organisation's working day, week and year, and the currency its money
   * figures are stated in. `null` when it has never stated them — a first-class
   * answer, not an empty object, because the absence is what
   * `GetRoleCoverageUseCase` reports as `NO_WORKING_TIME_MODEL`.
   *
   * NOT UNDER `/api/roles`, AND THAT IS DELIBERATE RATHER THAN A NAMING
   * PREFERENCE. `RolesController` declares `@Get(":id")`, which matches ANY
   * single segment under `/roles`, so a sibling `GET /api/roles/automation-
   * settings` would be in a race decided by module-container INSERTION order —
   * which `roles-static-routes-win-over-the-id-catch-all.spec.ts` records is
   * NOT the order of `AppModule.imports` and cannot be reasoned about from the
   * source. A route that cannot enter the race needs no rule to win it.
   *
   * It stays in `ZRoles` because the coverage screen is its only consumer and
   * the generated client groups by manifest, not by URL prefix.
   */
  GetOrganizationAutomationSettings: {
    method: "GET" as const,
    path: "/api/organization-automation-settings",
    Response: ApiSuccessSchema(OrganizationAutomationSettingsViewSchema.nullable())
  },

  /**
   * PUT /api/organization-automation-settings
   *
   * State the organisation's working-time model and currency.
   *
   * CREATE-OR-UPDATE, NEVER UPDATE-ONLY. `organizationId` is the primary key of
   * `OrganizationAutomationSettings`, so an organisation that has never stated
   * its working year has NO ROW — and a missing row produces exactly the same
   * `NO_WORKING_TIME_MODEL` / `NO_CURRENCY` answers as a null column. An
   * update-only write would therefore succeed at changing nothing, for every
   * organisation that has never been configured, which is all of them.
   *
   * ORG-ADMIN, NOT `coverage.manage`. Every Role in the organisation divides by
   * these three numbers — 46 working weeks against 52 moves every coverage
   * figure by 13% — so the authority is org-wide and the guard that enforces it
   * is `OrgAdminGuard`. `coverage.manage` is Role-scoped by construction and
   * its guard resolves a Role from the path; there is no Role here to resolve.
   */
  UpsertOrganizationAutomationSettings: {
    method: "PUT" as const,
    path: "/api/organization-automation-settings",
    Body: OrganizationAutomationSettingsInputSchema,
    Response: ApiSuccessSchema(OrganizationAutomationSettingsViewSchema)
  },

  /**
   * PUT /api/roles/:id/workload
   *
   * Author this Role's denominator, its own annual cost, its own revenue, and
   * its overrides of the organisation's working-time model.
   *
   * ONE ROW, ONE WRITE. `RoleWorkload.roleId` is `@unique`, so this is an
   * upsert on the Role and never a create-many — a second row would give one
   * Role two different coverage percentages depending on query order.
   *
   * A `PUT` OVER THE WHOLE ROW, so every optional model is required and
   * nullable. Sending `"costFormula": null` clears the cost model and means it;
   * there is no spelling that means "leave whatever was there". See the input
   * schema's header for why an omittable key is the one shape this must not
   * have.
   *
   * 404 for a Role that is not this organisation's, exactly as `Get` does.
   */
  UpsertRoleWorkload: {
    method: "PUT" as const,
    path: "/api/roles/:id/workload",
    PathVars: RolePathVarsSchema,
    Body: RoleWorkloadInputSchema,
    Response: ApiSuccessSchema(RoleWorkloadViewSchema)
  },

  /**
   * PUT /api/roles/:id/impacts/:roleResourceId
   *
   * Author what ONE system attached to this Role saves it, earns it and costs
   * it.
   *
   * KEYED ON THE `RoleResource` ROW, not on the polymorphic pair, because that
   * row id is what the coverage read already hands the client: every
   * contribution's `id` and every warning's `subject.id` IS this value, so a UI
   * repairing a flagged row has the key in hand and needs no lookup.
   * `RoleSystemImpact.roleResourceId` is `@unique`, so there is exactly one row
   * per system and this is an upsert on it.
   *
   * BOTH SEGMENTS ARE CHECKED AGAINST THE CALLER'S ORGANISATION, and the `:id`
   * is not decoration: the write refuses a `roleResourceId` that belongs to
   * another Role of the same organisation, which is the case a
   * `roleResourceId`-only route would happily accept and mis-attribute.
   *
   * 404 when the Role is not this organisation's, and 404 when the resource is
   * not attached to that Role — never 403, for the reason `role.errors.ts`
   * gives: telling a caller "not yours" confirms the row exists.
   */
  UpsertRoleSystemImpact: {
    method: "PUT" as const,
    path: "/api/roles/:id/impacts/:roleResourceId",
    PathVars: RoleSystemImpactPathVarsSchema,
    Body: RoleSystemImpactInputSchema,
    Response: ApiSuccessSchema(RoleSystemImpactViewSchema)
  },

  /**
   * PUT /api/roles/:id/systems/:roleResourceId/lifecycle
   *
   * Move ONE system between coverage buckets — `BUILDING | LIVE | RETIRED`.
   *
   * KEYED ON THE `RoleResource` ROW for the reason `UpsertRoleSystemImpact`
   * above gives, and it reuses that route's path-vars schema unchanged: the two
   * segments mean exactly the same two things, and a second declaration is a
   * second place for the `:id` check to be dropped.
   *
   * ── `role.update`, NOT `coverage.manage` ───────────────────────────────────
   *
   * The same authority `RoleSystemPolicy`'s own read and write carry, and that
   * is the argument rather than a coincidence: `requireReview` lives on that
   * policy, this route is its first enforcement reader, and a gate in a
   * DIFFERENT authority domain from the rule it enforces lets a holder of the
   * other capability move a system live under a rule its owners cannot see them
   * escape.
   *
   * ⚠️ `coverage.manage` was a real candidate — it is the live gate on five v1
   * writes — so this is a decided fork. Its catalog docblock scopes it to the
   * workload model, the impact models and the working-time assumptions
   * (magnitudes), and separates it from `resource.attach` because putting a
   * system in a Role and pricing it are different claims. A lifecycle
   * transition is neither: it says whether a system is doing the Role's work
   * yet.
   *
   * ── THE BODY CANNOT EXPRESS WHICH MOVES ARE LEGAL ──────────────────────────
   *
   * `RoleSystemLifecycleBodySchema` refuses a value that is not a lifecycle and
   * nothing more. Whether the move is an EDGE depends on the row's current
   * state, which the request has never seen, so it is answered server-side:
   * 400 `ROLE_SYSTEM_LIFECYCLE_TRANSITION_NOT_ALLOWED` for a non-edge, 409
   * `ROLE_SYSTEM_LIFECYCLE_UNCHANGED` when it is already there, 409
   * `ROLE_SYSTEM_LIFECYCLE_CONFLICT` when the row moved underneath the request,
   * and 403 `ROLE_SYSTEM_REVIEW_REQUIRED` when the Role requires a second
   * person and the caller is the submitter.
   *
   * 404 when the Role is not this organisation's, and 404 when the system is
   * not attached to that Role — never 403, for the reason `role.errors.ts`
   * gives: telling a caller "not yours" confirms the row exists.
   *
   * 🚨 THIS MOVES A CUSTOMER-FACING FIGURE. Only `LIVE` systems are summed into
   * `impactPersonHours` and `money.totals`, so a transition changes the Role's
   * published coverage. That is the point — it is the one thing on this surface
   * that does it without touching a single model.
   */
  TransitionRoleSystemLifecycle: {
    method: "PUT" as const,
    path: "/api/roles/:id/systems/:roleResourceId/lifecycle",
    PathVars: RoleSystemImpactPathVarsSchema,
    Body: RoleSystemLifecycleBodySchema,
    Response: ApiSuccessSchema(RoleSystemLifecycleResponseSchema)
  },

  // ═══════════════════════════════════════════════════════════════
  // RESPONSIBILITIES — what the Role is answerable for, in its author's
  // own words
  // ═══════════════════════════════════════════════════════════════

  /**
   * GET /api/roles/:id/responsibilities
   *
   * One Role's responsibilities, in order. Two segments, so it is not in the `@Get(":id")`
   * catch-all race at all and needs no pin.
   *
   * READ BY ANYONE WHO CAN SEE THE ROLE — `role.view`. A responsibility is a statement
   * about the Role itself rather than about its numbers, which is the same
   * argument `GetRoleSystemPolicy` makes for declaring `role.view` instead of a
   * `coverage.*` capability.
   */
  ListRoleResponsibilities: {
    method: "GET" as const,
    path: "/api/roles/:id/responsibilities",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleResponsibilitiesViewSchema)
  },

  /**
   * POST /api/roles/:id/responsibilities
   *
   * Add one responsibility. The server assigns `position` at the END of the list, so a
   * client cannot claim a slot or leave a hole, and two concurrent adds cannot
   * argue about who is third.
   *
   * 🚨 ONE RESPONSIBILITY PER REQUEST, AND THIS IS NOT AN OVERSIGHT. The alternative was
   * the whole-array `PUT` that `ReplaceRoleScopeLines` and
   * `ReplaceRoleVariables` use, and `role-responsibility.schema.ts` carries the
   * full argument for departing from it. The short version: step 2's own
   * shipped copy promises "Tasks link back to these", and a replace destroys
   * and re-mints every `id` on every save, so that link could never be built.
   *
   * The wizard therefore flushes N of these sequentially after the Role exists,
   * exactly as `addTeam` already does for members. Round trips are what this
   * costs; a referenceable row is what it buys.
   */
  AddRoleResponsibility: {
    method: "POST" as const,
    path: "/api/roles/:id/responsibilities",
    PathVars: RolePathVarsSchema,
    Body: RoleResponsibilityInputSchema,
    Response: ApiSuccessSchema(RoleResponsibilityViewSchema)
  },

  /**
   * PUT /api/roles/:id/responsibilities/:responsibilityId
   *
   * Re-word ONE responsibility, in place, keeping its `id`.
   *
   * ══════════════════════════════════════════════════════════════════════════
   * 🚨 THIS ROUTE EXISTS BECAUSE ITS ABSENCE WAS A SILENT DATA-LOSS PATH, AND
   * THE WORKAROUND IS THE DEFECT. READ THIS BEFORE "SIMPLIFYING" IT.
   * ══════════════════════════════════════════════════════════════════════════
   *
   * With only `POST` and `DELETE` here, an author correcting a typo had exactly
   * one route: delete the responsibility and add it back. That is not an edit — it MINTS
   * A NEW `id`, and `RoleTaskResponsibility.responsibility` is
   * `@relation(fields: [responsibilityId, roleId], references: [id, roleId],
   * onDelete: Cascade)`. So the DELETE cascades away every task tick that
   * referenced the responsibility, the POST returns `201` with a perfectly correct body,
   * and nothing is logged. The author is told the fix worked and the Role's
   * checklist has quietly lost its links.
   *
   * An UPDATE writes `text` and touches neither `id` nor `roleId`, so the
   * foreign key is never re-evaluated and every link row survives by
   * construction. That is the whole point of the route and the reason it must
   * never be implemented as a delete-and-recreate behind a new verb — doing so
   * reproduces the bug with a `PUT` on the front of it.
   *
   * ── `PUT` RATHER THAN `PATCH`, AND `text` IS THE WHOLE DOCUMENT ────────────
   *
   * `RoleResponsibilityInputSchema` has exactly one field, so a partial update
   * and a total one are the same request and `PATCH` would only invite a
   * `preserveWhenOmitted` question this resource does not have. `position` is
   * deliberately NOT accepted: it is server-assigned on add and there is no
   * reorder endpoint, which `role-responsibility.schema.ts` records as a
   * measured `debt:` rather than an oversight. A `PUT` that silently ignored a
   * `position` a caller sent would be worse than refusing it, and the schema is
   * `strictObject`, so it refuses.
   *
   * ── THE SAME TWO 404s AS `DELETE`, FOR THE SAME REASONS ───────────────────
   *
   * Both path segments are checked against the caller's organisation, and the
   * `:id` is load-bearing: a `responsibilityId`-only route would re-word a responsibility
   * belonging to a DIFFERENT Role of the same organisation. 404 rather than 403
   * throughout — telling a caller "not yours" confirms the row exists.
   *
   * `role.update` rather than `coverage.manage`, matching `POST` and `DELETE`
   * beside it: a responsibility is a statement about the Role itself, not about its
   * numbers.
   */
  UpdateRoleResponsibility: {
    method: "PUT" as const,
    path: "/api/roles/:id/responsibilities/:responsibilityId",
    PathVars: RoleResponsibilityPathVarsSchema,
    Body: RoleResponsibilityInputSchema,
    Response: ApiSuccessSchema(RoleResponsibilityViewSchema)
  },

  /**
   * DELETE /api/roles/:id/responsibilities/:responsibilityId
   *
   * Remove one responsibility, named explicitly. There is no request in this resource
   * whose success is indistinguishable from destroying everything else — which
   * is the second reason this is not a whole-array replace.
   *
   * BOTH SEGMENTS ARE CHECKED AGAINST THE CALLER'S ORGANISATION, and the `:id`
   * is load-bearing: a `responsibilityId`-only route would accept a responsibility
   * belonging to a DIFFERENT Role of the same organisation and delete it.
   *
   * ⚠️ REMOVING A RESPONSIBILITY LEAVES A HOLE IN `position` — 0, 1, 3 — and nothing
   * backfills it. `position` is an insertion order, not a dense rank; the read
   * orders on `(position, createdAt, id)`, which stays total either way.
   *
   * 404 when the Role is not this organisation's, and 404 when the responsibility is not
   * that Role's — never 403, for the reason `role.errors.ts` gives: telling a
   * caller "not yours" confirms the row exists.
   */
  RemoveRoleResponsibility: {
    method: "DELETE" as const,
    path: "/api/roles/:id/responsibilities/:responsibilityId",
    PathVars: RoleResponsibilityPathVarsSchema,
    Response: ApiSuccessSchema(z.object({ id: z.string() }))
  },

  // ═══════════════════════════════════════════════════════════════
  // THE SCOPE MODEL — the org-wide job-type library, and what one Role
  // states on top of it
  // ═══════════════════════════════════════════════════════════════

  /**
   * GET /api/organization-job-types
   *
   * The organisation's job-type library: every way of paying for work that any
   * Role in the organisation can quantify.
   *
   * ── WHY NOT `/api/roles/job-types` ──────────────────────────────────────
   *
   * `RolesController` declares `@Get(":id")`, which matches ANY single segment
   * under `/roles`. A sibling `GET /api/roles/job-types` would be in a race
   * with it decided by module-container INSERTION order — which
   * `roles-static-routes-win-over-the-id-catch-all.spec.ts` records is NOT the
   * order of `AppModule.imports` and cannot be reasoned about from the source.
   * `GET /api/roles/system-map` and `GET /api/roles/management` have both died
   * that way, and live HTTP cannot see it: the global guard answers before any
   * handler, so a shadowed route and a correct one are byte-identical on the
   * wire. A route that cannot enter the race needs no rule to win it.
   *
   * ── WHY NOT UNDER A ROLE AT ALL ─────────────────────────────────────────
   *
   * The library is ORG-SCOPED. Per-Role would re-key every rate for every Role
   * and defeat the point of a library; `RoleScopeLine`'s foreign key into it is
   * composite on `(jobTypeId, organizationId)` precisely so that quantifying
   * another tenant's job type is unrepresentable rather than merely refused.
   *
   * READ BY ANY MEMBER, WRITTEN BY AN ADMIN. Every Role's Scope tab lists this,
   * so gating the read on org-admin would blank the screen for everybody else.
   */
  ListOrganizationJobTypes: {
    method: "GET" as const,
    path: "/api/organization-job-types",
    Response: ApiSuccessSchema(RoleJobTypeLibraryViewSchema)
  },

  /**
   * POST /api/organization-job-types
   *
   * Add a way of paying for work to the organisation's library.
   *
   * ORG-ADMIN, the same authority `UpsertOrganizationAutomationSettings`
   * carries and for the same reason: one row here prices every scope line in
   * every Role that quantifies it, so the authority is org-wide.
   * `RoleCapabilityGuard` is Role-scoped by construction — it resolves a Role
   * from the request's params — and there is no Role here to resolve.
   *
   * `position` is assigned by the server, at the end of the library.
   */
  CreateOrganizationJobType: {
    method: "POST" as const,
    path: "/api/organization-job-types",
    Body: RoleJobTypeInputSchema,
    Response: ApiSuccessSchema(RoleJobTypeViewSchema)
  },

  /**
   * PUT /api/organization-job-types/:jobTypeId
   *
   * Restate one job type. A `PUT` over the whole row, so every optional field
   * is required and nullable: sending `"note": null` clears the note and means
   * it, and there is no spelling that means "leave whatever was there".
   *
   * 404 for a job type that is not this organisation's, never 403 — telling a
   * caller "not yours" confirms the row exists.
   */
  UpdateOrganizationJobType: {
    method: "PUT" as const,
    path: "/api/organization-job-types/:jobTypeId",
    PathVars: RoleJobTypePathVarsSchema,
    Body: RoleJobTypeInputSchema,
    Response: ApiSuccessSchema(RoleJobTypeViewSchema)
  },

  /**
   * DELETE /api/organization-job-types/:jobTypeId
   *
   * Remove a job type from the library.
   *
   * ⚠️ REFUSED WHILE ANY SCOPE LINE STILL QUANTIFIES IT — `409`, with the count
   * of lines that do. `RoleScopeLine`'s foreign key is `NO ACTION`, so the
   * database refuses it anyway with a `23503`; catching it first turns a 500
   * into an answer the author can act on ("four lines still use this").
   */
  DeleteOrganizationJobType: {
    method: "DELETE" as const,
    path: "/api/organization-job-types/:jobTypeId",
    PathVars: RoleJobTypePathVarsSchema,
    Response: ApiSuccessSchema(z.object({ id: z.string() }))
  },

  /**
   * GET /api/roles/:id/scope-lines
   *
   * One Role's Scope, in order. Two segments, so it is not in the `:id`
   * catch-all race at all and needs no pin.
   */
  ListRoleScopeLines: {
    method: "GET" as const,
    path: "/api/roles/:id/scope-lines",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleScopeLinesViewSchema)
  },

  /**
   * PUT /api/roles/:id/scope-lines
   *
   * State the Role's whole Scope in one request. The array's index IS the
   * position, so a reorder is this same request with the elements moved — and
   * there is no `.../order` literal to put a single segment back in the race.
   *
   * REPLACE, NOT UPSERT. `RoleScopeLine` has no unique constraint to upsert on,
   * deliberately: two lines may point at one job type with different `scope`
   * text (six seats in Manila, four in Lisbon), and a unique on
   * `(roleId, jobTypeId)` would make that ordinary case unrepresentable.
   *
   * ⚠️ ROW IDS DO NOT SURVIVE. The replace deletes and re-inserts inside one
   * transaction; nothing references a scope line by id.
   */
  ReplaceRoleScopeLines: {
    method: "PUT" as const,
    path: "/api/roles/:id/scope-lines",
    PathVars: RolePathVarsSchema,
    Body: RoleScopeLinesInputSchema,
    Response: ApiSuccessSchema(RoleScopeLinesViewSchema)
  },

  /**
   * GET /api/roles/:id/tasks
   *
   * One Role's proposed tasks, in order — the work it runs day to day, named
   * before any system does it. Two segments, so it is not in the `:id`
   * catch-all race at all and needs no pin.
   *
   * READ BY ANY MEMBER who can see the Role. A task is a description of the
   * Role's own work; gating it on the coverage capability would blank the
   * wizard's own review step for the person creating the Role.
   */
  ListRoleTasks: {
    method: "GET" as const,
    path: "/api/roles/:id/tasks",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleTasksViewSchema)
  },

  /**
   * PUT /api/roles/:id/tasks
   *
   * State the Role's whole task list in one request. The array's index IS the
   * position, so a reorder is this same request with the elements moved — and
   * there is no `.../order` literal to put a single segment back in the race.
   *
   * ⚠️ REPLACE, NOT MERGE. A one-element body deletes every other task and
   * returns 200. `RoleTasksInputSchema` owns the full reasoning; the hazard is
   * repeated here because this is the descriptor a caller reads first.
   *
   * ⚠️ EVERY ID IN THE BODY IS VERIFIED AGAINST THE CALLER'S ORGANISATION
   * BEFORE ANY ROW IS WRITTEN. An assignment naming another tenant's user or
   * another tenant's workflow is a 400, not a stored row — `ReplaceRoleTasksUseCase`
   * is where that happens, and the Zod schema deliberately does NOT claim to do
   * it: shape and ownership are different questions and only one of them can be
   * answered without a query.
   */
  ReplaceRoleTasks: {
    method: "PUT" as const,
    path: "/api/roles/:id/tasks",
    PathVars: RolePathVarsSchema,
    Body: RoleTasksInputSchema,
    Response: ApiSuccessSchema(RoleTasksViewSchema)
  },

  /**
   * POST /api/roles/:id/tasks/:taskId/graduate
   *
   * Turn one PROPOSED task into a system the Role holds and prices, and drop the
   * proposal. `role-task.schema.ts` names this transition in the model's own
   * words — a task *"graduates by becoming a `RoleResource` + `RoleSystemImpact`
   * pair"* — and until now nothing performed it: both halves shipped as separate
   * endpoints, so the act existed only as three manual steps.
   *
   * ── IT COMPOSES TWO EXISTING WRITES AND INVENTS NEITHER ─────────────────────
   *
   * `POST /api/roles/:id/resources` attaches, `PUT /api/roles/:id/impacts/:rrId`
   * prices, and this route performs exactly those two and then deletes the
   * proposal. Nothing here is a second implementation of either: the attach goes
   * through `AssignResourceUseCase` — the only writer of `RoleResource`, and the
   * one carrying the permission-sync compensation — and the pricing goes through
   * the same `UPSERT_ROLE_SYSTEM_IMPACT_PORT`.
   *
   * 🚨 IT IS GATED ON BOTH CAPABILITIES ITS TWO HALVES CARRY, `resource.attach`
   * AND `coverage.manage`, AND THAT CONJUNCTION IS THE POINT. `role-capability.types.ts`
   * states that pricing a system is *"deliberately separate from `resource.attach`
   * — putting a system into a Role and stating how much work that system removes
   * are different claims, made by different people"*, and that the split exists
   * *"so a custom group can hold one without the other"*. One action making both
   * claims must therefore hold both; gating on either alone would let a holder of
   * one perform the other, and attaching writes real `ResourcePermission` grants.
   *
   * ⚠️ NOT ATOMIC, DELIBERATELY, AND THE ORDER IS THE SAFETY PROPERTY. The three
   * writes cannot share a transaction — the attach spans a Prisma write and a
   * permission sync across another module's ports, with its own compensation
   * block. So they run attach → price → drop, and every partial state is one the
   * product already renders and nobody loses authored work by:
   *
   *   stops after the attach  the Role holds an UNPRICED system (`unmodelledSystems`,
   *                           the state `UnvaluedSystemsList` exists to draw) and
   *                           the proposal is still there — retry converges,
   *                           both writes being upserts
   *   stops after the pricing the system is held and priced and the proposal is
   *                           still listed — a visible duplicate the author deletes
   *
   * Dropping the proposal FIRST is the one order that can destroy work: the task
   * carries a name, a description and three authored figures that exist nowhere
   * else, so a failed attach after a successful delete loses them with no record.
   * `graduate-role-task.use-case.spec.ts` pins the order.
   *
   * ⚠️ `taskId` SURVIVES A `PUT /tasks` THAT NAMES THE TASK, and not one that
   * omits it — `RoleTasksInputSchema` carries the rule. A task the save leaves
   * out is deleted, so a graduation racing that save answers 404 and the client
   * refetches; it can never graduate the wrong task, because an id that no
   * longer exists matches nothing.
   *
   * RETURNS THE STORED IMPACT, which carries `roleResourceId` — the id of the
   * attachment that was just created, and the same id every coverage warning
   * names. A caller needs it to address the new card without re-reading the map.
   */
  GraduateRoleTask: {
    method: "POST" as const,
    path: "/api/roles/:id/tasks/:taskId/graduate",
    PathVars: RoleTaskGraduationPathVarsSchema,
    Body: RoleTaskGraduationInputSchema,
    Response: ApiSuccessSchema(RoleSystemImpactViewSchema)
  },

  /**
   * GET /api/roles/:id/tasks/:taskId/responsibilities
   *
   * Which of the Role's responsibilities this task covers — the ticks in the prototype's
   * "Responsibilities it covers" checklist.
   *
   * THREE SEGMENTS, so it is nowhere near `RolesController`'s `@Get(":id")`
   * catch-all and needs no pin. The two-segment note on the routes above applies
   * a fortiori.
   *
   * 🚨 IDS ONLY, NEVER THE RESPONSIBILITY'S TEXT, AND THAT IS WHAT MAKES `coverage.view`
   * THE HONEST GATE. `role-task-responsibility.schema.ts` carries the full
   * argument. In short: the text has one home (`ListRoleResponsibilities`, gated
   * `role.view`), the checklist has already read every responsibility before it can draw a
   * single tick, and a payload of opaque ids is a statement about the TASK —
   * which is exactly what `coverage.view` governs, and what `ListRoleTasks`
   * beside it already declares. Serving responsibility TEXT here would be a `role.view`
   * subject behind a `coverage.view` gate, which is a real leak and the reason a
   * conjunction of the two was once proposed.
   *
   * ORDERED BY THE RESPONSIBILITY'S OWN `(position, createdAt, id)`, not by the link and
   * not by click order — the link table carries no position by design.
   *
   * 404 when the Role is not this organisation's, and 404 when the task is not
   * that Role's — never 403, for the reason `role.errors.ts` gives: telling a
   * caller "not yours" confirms the row exists.
   */
  ListRoleTaskResponsibilities: {
    method: "GET" as const,
    path: "/api/roles/:id/tasks/:taskId/responsibilities",
    PathVars: RoleTaskResponsibilityPathVarsSchema,
    Response: ApiSuccessSchema(RoleTaskResponsibilitiesViewSchema)
  },

  /**
   * PUT /api/roles/:id/tasks/:taskId/responsibilities
   *
   * State the whole set of responsibilities this task covers, in one request.
   *
   * ⚠️ REPLACE, NOT MERGE. An empty array unlinks everything and answers 200 —
   * which is the correct body for "untick the last box", not an accident. The
   * grain matches `PUT /api/roles/:id/tasks` beside it: a checklist is
   * set-authoring, so one request per tick would make a half-applied checklist a
   * reachable state.
   *
   * ⚠️ EVERY ID IN THE BODY IS VERIFIED AGAINST THIS ROLE BEFORE ANY ROW IS
   * WRITTEN. A responsibility of another Role — or of another tenant — is a 400, not a
   * stored row. `ReplaceRoleTaskResponsibilitiesUseCase` is where that happens,
   * and the schema deliberately makes no such claim: shape and ownership are
   * different questions and only one of them can be answered without a query.
   *
   * WRITE IS `coverage.manage`, NOT `role.update`. A link writes ONLY the join
   * row: it creates, deletes and rewords nothing on either endpoint, so it is
   * not `role.update`'s claim ("rename the Role, rewrite its job description").
   * It authors a property OF THE TASK, structurally identical to
   * `RoleTaskAssignment` — and referencing a row you may already read is not a
   * write claim on that row.
   */
  ReplaceRoleTaskResponsibilities: {
    method: "PUT" as const,
    path: "/api/roles/:id/tasks/:taskId/responsibilities",
    PathVars: RoleTaskResponsibilityPathVarsSchema,
    Body: RoleTaskResponsibilitiesInputSchema,
    Response: ApiSuccessSchema(RoleTaskResponsibilitiesViewSchema)
  },

  /**
   * GET /api/roles/:id/variables
   *
   * The Role's shared assumptions — the named numbers every job type's parts
   * resolve against.
   */
  ListRoleVariables: {
    method: "GET" as const,
    path: "/api/roles/:id/variables",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleVariablesViewSchema)
  },

  /**
   * PUT /api/roles/:id/variables
   *
   * State the Role's whole set of assumptions in one request, in order.
   *
   * 🚨 THIS IS WHAT MAKES THE SCOPE A MODEL. A job type's part either names a
   * variable or carries a literal; every part that names one re-prices the
   * moment this endpoint is called. Without it, `parts[].source.kind ===
   * "variable"` resolves to nothing and the library collapses into per-type
   * literals — the screen still renders and has stopped being a model.
   */
  ReplaceRoleVariables: {
    method: "PUT" as const,
    path: "/api/roles/:id/variables",
    PathVars: RolePathVarsSchema,
    Body: RoleVariablesInputSchema,
    Response: ApiSuccessSchema(RoleVariablesViewSchema)
  },

  /**
   * GET /api/roles/:id/working-year
   *
   * The four terms this Role's working year is built from, or `null` when the
   * Role has never stated one. `null` crosses as `null` — not `{}`, and not a
   * default 52/5/11/6, which is a French statutory year and would misprice
   * every other jurisdiction while looking like a filled-in form.
   */
  GetRoleWorkingYear: {
    method: "GET" as const,
    path: "/api/roles/:id/working-year",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleWorkingYearViewSchema.nullable())
  },

  /**
   * PUT /api/roles/:id/working-year
   *
   * State the calendar year, the paid leave, the public holidays and the
   * sickness this Role assumes.
   *
   * CREATE-OR-UPDATE. `RoleWorkingYear.roleId` is `@unique`, so a Role that has
   * never stated one has NO ROW, and an update-only write would succeed at
   * changing nothing for every Role — which is all of them today.
   *
   * ADDITIVE, NOT AUTHORITATIVE. `RoleWorkload`'s overrides and
   * `OrganizationAutomationSettings` stay what the coverage engine reads; this
   * records what a working year was derived FROM so each term is arguable.
   */
  UpsertRoleWorkingYear: {
    method: "PUT" as const,
    path: "/api/roles/:id/working-year",
    PathVars: RolePathVarsSchema,
    Body: RoleWorkingYearInputSchema,
    Response: ApiSuccessSchema(RoleWorkingYearViewSchema)
  },

  /**
   * GET /api/roles/:id/system-policy
   *
   * How systems inside this Role get proposed, reviewed, started and pushed, or
   * `null` when nobody has stated a policy.
   *
   * `null` rather than the column defaults: "nobody has chosen" and "somebody
   * chose the shipped posture" are different facts, and only the first one lets
   * a screen show its defaults as defaults.
   */
  GetRoleSystemPolicy: {
    method: "GET" as const,
    path: "/api/roles/:id/system-policy",
    PathVars: RolePathVarsSchema,
    Response: ApiSuccessSchema(RoleSystemPolicyViewSchema.nullable())
  },

  /**
   * PUT /api/roles/:id/system-policy
   *
   * State all five switches. The columns carry defaults so a sixth switch is
   * addable without a backfill — not so a caller can leave a policy unstated.
   */
  UpsertRoleSystemPolicy: {
    method: "PUT" as const,
    path: "/api/roles/:id/system-policy",
    PathVars: RolePathVarsSchema,
    Body: RoleSystemPolicyInputSchema,
    Response: ApiSuccessSchema(RoleSystemPolicyViewSchema)
  },

  /**
   * GET /api/organization-roles-rollup
   *
   * The Roles index screen in one org-scoped read: every Role with its name,
   * description, owner, system count, health and per-Role figures, plus the
   * totals across them.
   *
   * ── WHY NOT `/api/roles/rollup` ────────────────────────────────────────────
   *
   * `RolesController` declares `@Get(":id")`, which matches ANY single segment
   * under `/roles`. A sibling literal there is in a race decided by
   * module-container INSERTION order, and that race is invisible: the global
   * guard answers 403 before any handler runs, so a shadowed route and a
   * correct one are byte-identical on the wire. `GET /api/roles/system-map` and
   * `GET /api/roles/management` were both dead that way.
   *
   * The three literals that remain in the race are kept alive by their
   * DECLARATION ORDER inside `RolesController` and pinned by a spec that boots
   * a real router to watch them win. That is a rule to keep, not a property to
   * rely on.
   *
   * 🚨 THIS PATH CANNOT ENTER THE RACE AT ALL, AND THAT IS STRUCTURAL RATHER
   * THAN ORDERED. To shadow a one-segment literal directly under `/api`, some
   * route would have to match exactly one segment at that depth — either
   * `@Controller()` with `@Get(":x")`, or `@Controller(":x")` with `@Get()`.
   * Neither exists anywhere in the backend: every empty-prefix controller
   * declares a multi-segment literal first segment, and every parameterised
   * prefix begins with a literal. So no registration order can produce a
   * competitor, and no future reorder of `RolesController` can kill this route.
   *
   * It also names no `:id`, so it is outside the class of `/api/roles/:id/*`
   * routes that 500 on a malformed Role id.
   *
   * It stays in `ZRoles` because the Roles screen is its only consumer and the
   * generated client groups by manifest, not by URL prefix — the same reasoning
   * `GetOrganizationAutomationSettings` records above.
   *
   * ── OPEN TO EVERY MEMBER ───────────────────────────────────────────────────
   *
   * A read, org-scoped by the JWT, and NOT admin-guarded: it is the index every
   * member of the organisation lands on. Adding `OrgAdminGuard` here would blank
   * the screen for every non-admin silently, so the absence of that guard is
   * pinned by signature in the governance spec rather than left to a reviewer.
   */
  GetOrganizationRolesRollup: {
    method: "GET" as const,
    path: "/api/organization-roles-rollup",
    Response: ApiSuccessSchema(OrgRolesRollupViewSchema)
  },

  /**
   * GET /api/organization-roles-run-health
   *
   * Whether each Role's systems are actually RUNNING — a census of real
   * top-level workflow runs over a served window, per Role and org-wide.
   *
   * ── WHY IT IS A SECOND READ AND NOT A FIELD ON THE ROLLUP ──────────────────
   *
   * The rollup answers "what has been MODELLED"; this answers "what has RUN".
   * Different tables, different rates of change, and this one scans the
   * execution history of every workflow the organisation owns — which does not
   * belong on the critical path of the index screen's first paint.
   *
   * 🚨 IT IS ALSO THE ANSWER TO A QUESTION THE ROLLUP CANNOT ANSWER, AND THE
   * NEAR-MISS IS THE WHOLE POINT. `totals.needAttention` over there counts Roles
   * whose COVERAGE MODEL could not be evaluated. It is not a count of failing
   * runs, and an `# ISSUES` card fed from it publishes a plausible integer
   * against the wrong question. `totals.runs.failed` here is the honest figure.
   *
   * ── THE SAME ROUTER ARGUMENT AS THE ROLLUP ─────────────────────────────────
   *
   * Not `/api/roles/run-health`: `RolesController`'s `@Get(":id")` matches any
   * single segment under `/roles`, and that race is settled by container
   * insertion order and is invisible from outside. A one-segment literal
   * directly under `/api` has no possible competitor in this backend, so this
   * path cannot enter the race at all. It stays in `ZRoles` because the Roles
   * screen is its only consumer and the generated client groups by manifest.
   *
   * ── SCOPE, STATED ON THE CONTRACT ──────────────────────────────────────────
   *
   * Workflows only, and the payload says so in `systemsObservable` /
   * `systemsUnobservable`. Four of the five Role-ownable kinds have no execution
   * record in this product; `SkillExecution` looks like the AI-task one and is
   * not, because its only writer hardcodes `isTest: true`. The view schema
   * carries the full table.
   *
   * Org-scoped by the JWT. There is no identifier of any kind in this contract.
   */
  GetOrganizationRolesRunHealth: {
    method: "GET" as const,
    path: "/api/organization-roles-run-health",
    Response: ApiSuccessSchema(OrgRolesRunHealthViewSchema)
  },

  /**
   * GET /api/organization-roles-system-integrity
   *
   * Whether the systems each Role holds can WORK AT ALL — a per-system
   * integrity verdict across all five `RoleResourceType` kinds, per Role and
   * org-wide.
   *
   * ── WHY IT IS A THIRD READ AND NOT A FIELD ON RUN HEALTH ───────────────────
   *
   * Run health asks *did it FAIL when it ran*; this asks *can it WORK at all*.
   * The two are not shades of one question, and the near-miss is the point: a
   * `RoleResource` pointing at a DELETED workflow reads as `idle` over there,
   * byte-identical in the payload to a live workflow nobody ran. Run health's
   * own schema states *"IDLE IS NOT HEALTHY AND IT IS NOT BROKEN"* — a correct
   * refusal, because outcome data structurally cannot separate those two. Run
   * health also covers `workflow` alone, the only kind recording an outcome;
   * this covers all five.
   *
   * ── THE SAME ROUTER ARGUMENT AS ITS TWO SIBLINGS ───────────────────────────
   *
   * Not `/api/roles/system-integrity`: `RolesController`'s `@Get(":id")` matches
   * any single segment under `/roles`, and that race is settled by container
   * insertion order and is invisible from outside — the global guard answers
   * 403 before any handler runs, so a shadowed route and a working one are
   * byte-identical on the wire. Two Roles endpoints were dead that way. A
   * one-segment literal directly under `/api` cannot enter the race at all.
   *
   * ── THE READ RECOMPUTES; THE STORED ROW ONLY ANSWERS *SINCE WHEN* ──────────
   *
   * A customer who fixes a workflow at 09:00 must not see red until the sweep
   * runs that night — the classic health model that gets reported as a bug and
   * then ignored. So one probe set has two callers: this read answers *is it
   * broken NOW* and is never stale, and the nightly sweep's stored row supplies
   * `brokenSince`, which is unrecoverable retroactively and is the only thing
   * here that could not be added later.
   *
   * Org-scoped by the JWT. There is no identifier of any kind in this contract.
   */
  GetOrganizationRolesSystemIntegrity: {
    method: "GET" as const,
    path: "/api/organization-roles-system-integrity",
    Response: ApiSuccessSchema(OrgRolesSystemIntegrityViewSchema)
  }
} as const;
